import React, { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { api } from '../lib/api'
import Layout from '../components/Layout'
import { Card, Spinner, ErrorMessage, Button, Notice } from '../components/ui'
import { points, adp, formatLabel } from '../lib/format'
import SelectionChips from '../components/SelectionChips'
import {
  nextParticipant,
  destinationsFor,
  availableTeams,
  withoutParticipant,
  tradingTeamIds,
  idleParticipants,
} from '../lib/participants'

// Tailwind scans for whole class names, so these cannot be built from the
// participant count at runtime.
const COLUMN_GRID = {
  1: '',
  2: 'md:grid-cols-2',
  3: 'md:grid-cols-2 lg:grid-cols-3',
  4: 'md:grid-cols-2 lg:grid-cols-4',
}

function TeamColumn({ league, team, mine, selected, onToggle, onSync, syncing }) {
  const [search, setSearch] = useState('')

  const players = useQuery({
    queryKey: ['leaguePlayers', league.id, team?.id],
    queryFn: () => api.leaguePlayers(league.id, { teamId: team?.id }),
    enabled: !!team,
  })

  const all = players.data?.players || []

  // Client-side: a single roster is ~15 players, so filtering here is instant
  // and avoids a request per keystroke. The API's `q` param stays available
  // for any future league-wide search.
  const visible = useMemo(() => {
    if (!search.trim()) return all
    const needle = search.trim().toLowerCase()
    return all.filter(
      (p) =>
        p.name.toLowerCase().includes(needle) ||
        (p.position || '').toLowerCase() === needle ||
        (p.team || '').toLowerCase() === needle
    )
  }, [all, search])

  if (!team) return null

  return (
    <Card
      title={team.name}
      subtitle={mine ? 'Your team · tap to give up' : 'Tap to give up'}
    >
      <div className="mb-3 flex items-center gap-2">
        <input
          className="min-w-0 flex-1 rounded-md px-3 py-1.5 text-sm border border-slate-600 bg-slate-900"
          placeholder="Search name, position, or NFL team"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <span className="shrink-0 text-xs tabular-nums text-slate-400">
          {search.trim() ? `${visible.length}/${all.length}` : `${all.length}`}
        </span>
      </div>

      {players.isPending && <Spinner label="Loading roster…" />}
      <ErrorMessage error={players.error} />

      {/* Three genuinely different situations, which used to share one
          misleading message ("No players match."). */}
      {!players.isPending && !players.error && all.length === 0 && (
        <Notice
          title="No players on this roster"
          action={
            <Button variant="secondary" onClick={onSync} disabled={syncing}>
              {syncing ? 'Syncing…' : 'Sync league'}
            </Button>
          }
        >
          The league synced before its players did. Syncing again will fill in
          the rosters.
        </Notice>
      )}

      {!players.isPending && all.length > 0 && visible.length === 0 && (
        <p className="text-sm text-slate-400">
          No players match “{search.trim()}”.
        </p>
      )}

      {visible.length > 0 && (
        <ul className="max-h-96 space-y-1 overflow-y-auto">
          {visible.map((player) => {
            const active = selected.has(player.id)

            return (
              <li key={player.id}>
                <button
                  type="button"
                  onClick={() => onToggle(player, team)}
                  aria-pressed={active}
                  className={`flex w-full items-center justify-between rounded-md border px-2.5 py-1.5 text-left text-sm transition ${
                    active
                      ? 'border-emerald-500 bg-emerald-950/40'
                      : 'border-transparent hover:border-slate-600'
                  }`}
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <span
                      aria-hidden="true"
                      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border text-[10px] ${
                        active
                          ? 'border-emerald-600 bg-emerald-600 text-white'
                          : 'border-slate-600'
                      }`}
                    >
                      {active ? '✓' : ''}
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate text-slate-100">
                        {player.name}
                      </span>
                      <span className="block text-xs text-slate-400">
                        {player.position}
                        {player.team ? ` · ${player.team}` : ''} · ADP {adp(player.adp)}
                      </span>
                    </span>
                  </span>
                  <span className="ml-2 shrink-0 text-right text-xs tabular-nums text-slate-300">
                    {points(player.projected_points)}
                    <span className="block text-[10px] uppercase text-slate-400">proj</span>
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </Card>
  )
}

export default function TradeBuilder() {
  const { leagueId } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const league = useQuery({
    queryKey: ['league', leagueId],
    queryFn: () => api.league(leagueId),
  })

  // The teams in the deal, in the order they were added. A player's destination
  // defaults to the next team along, so a two-team swap needs no routing at all
  // and a three-way rotation needs none either.
  const [participantIds, setParticipantIds] = useState([])
  // player id -> { player, fromTeamId, toTeamId }. A Map so a player can only
  // be in the trade once, which mirrors the one-row-per-player unique index.
  const [selection, setSelection] = useState(new Map())

  const teams = league.data?.teams || []

  useEffect(() => {
    if (!league.data || participantIds.length > 0) return

    const mine = league.data.league.my_team_id || teams[0]?.id
    const other = teams.find((t) => t.id !== mine)?.id
    setParticipantIds([mine, other].filter(Boolean))
  }, [league.data, participantIds, teams])

  const sync = useMutation({
    mutationFn: () => api.syncLeague(leagueId),
    onSuccess: () => queryClient.invalidateQueries(),
  })

  const create = useMutation({
    mutationFn: () => {
      const assets = [...selection.values()].map(({ player, fromTeamId, toTeamId }) => ({
        player_id: player.id,
        from_team_id: fromTeamId,
        to_team_id: toTeamId,
      }))
      return api.createTrade(leagueId, assets)
    },
    onSuccess: (data) => navigate(`/trades/${data.trade.id}`),
  })

  if (league.isPending) {
    return (
      <Layout wide>
        <Spinner label="Loading league…" />
      </Layout>
    )
  }

  if (league.error) {
    return (
      <Layout wide>
        <ErrorMessage error={league.error} />
      </Layout>
    )
  }

  const info = league.data.league
  const health = info.data_health
  const maxTeams = info.max_trade_teams || 2
  const multiTeam = Boolean(info.supports_multi_team_trades)
  const myTeamId = info.my_team_id

  const byId = new Map(teams.map((t) => [t.id, t]))
  const participants = participantIds.map((id) => byId.get(id)).filter(Boolean)
  const nameFor = (id) => byId.get(id)?.name || 'a team'

  const toggle = (player, team) => {
    setSelection((current) => {
      const next = new Map(current)
      if (next.has(player.id)) {
        next.delete(player.id)
      } else {
        next.set(player.id, {
          player,
          fromTeamId: team.id,
          toTeamId: nextParticipant(participantIds, team.id),
        })
      }
      return next
    })
  }

  const route = (player, toTeamId) => {
    setSelection((current) => {
      const entry = current.get(player.id)
      if (!entry) return current

      const next = new Map(current)
      next.set(player.id, { ...entry, toTeamId })
      return next
    })
  }

  const remove = (player) => {
    setSelection((current) => {
      const next = new Map(current)
      next.delete(player.id)
      return next
    })
  }

  const addParticipant = () => {
    const candidate = availableTeams(teams, participantIds)[0]
    if (candidate) setParticipantIds([...participantIds, candidate.id])
  }

  const dropParticipant = (teamId) => {
    const result = withoutParticipant(participantIds, selection, teamId)
    setParticipantIds(result.participantIds)
    setSelection(result.selection)
  }

  // Swapping a team out takes its players with it, since they are no longer on
  // any roster in the deal; anything merely aimed at it is re-aimed.
  const changeParticipant = (index, teamId) => {
    const previous = participantIds[index]
    if (previous === teamId) return

    const updated = participantIds.map((id, i) => (i === index ? teamId : id))
    setParticipantIds(updated)
    setSelection((current) => {
      const next = new Map()
      current.forEach((entry, playerId) => {
        if (entry.fromTeamId === previous) return

        const toTeamId =
          entry.toTeamId === previous
            ? nextParticipant(updated, entry.fromTeamId)
            : entry.toTeamId

        if (toTeamId) next.set(playerId, { ...entry, toTeamId })
      })
      return next
    })
  }

  const chosen = [...selection.values()]
  const selectedIds = new Set(chosen.map((c) => c.player.id))
  const involved = tradingTeamIds(selection)
  const idle = idleParticipants(participantIds, selection)
  const ready = chosen.length > 0 && involved.length >= 2

  return (
    <Layout wide>
      <div className="mb-4">
        <h1 className="text-lg font-semibold text-slate-100">{info.name}</h1>
        <p className="text-sm text-slate-400">
          Week {league.data.current_week} · {formatLabel(info.format?.ppr_type)}
          {info.format?.superflex ? ' · Superflex' : ''}
        </p>
        <p className="mt-1 text-sm">
          <Link
            to={`/leagues/${leagueId}/explore`}
            className="underline underline-offset-2 text-slate-300 hover:text-slate-100"
          >
            Explore any players instead →
          </Link>
        </p>
      </div>

      {health && !health.projections_present && (
        <div className="mb-4">
          <Notice title={`No projections synced for week ${health.projected_week}`}>
            Points and win odds will all read zero until projections are pulled.
            Run <code className="font-mono">bin/rails sleeper:bootstrap</code>.
          </Notice>
        </div>
      )}

      <div className="mb-4">
        <div className={`grid gap-3 sm:grid-cols-2 ${COLUMN_GRID[participants.length] || ''}`}>
          {participantIds.map((id, index) => (
            <label key={id} className="block">
              <span className="mb-1 flex items-center justify-between text-xs font-medium text-slate-300">
                <span>
                  {index === 0 && id === myTeamId ? 'Your team' : `Team ${index + 1}`}
                </span>
                {/* Never below two: a trade has to have two sides. */}
                {participantIds.length > 2 && (
                  <button
                    type="button"
                    onClick={() => dropParticipant(id)}
                    className="text-slate-400 hover:text-rose-300"
                  >
                    Remove
                  </button>
                )}
              </span>
              <select
                className="w-full rounded-md px-3 py-2 text-sm border border-slate-600 bg-slate-900"
                value={id}
                onChange={(e) => changeParticipant(index, e.target.value)}
              >
                {teams
                  .filter((team) => team.id === id || !participantIds.includes(team.id))
                  .map((team) => (
                    <option key={team.id} value={team.id}>
                      {team.name}
                    </option>
                  ))}
              </select>
            </label>
          ))}
        </div>

        {multiTeam && (
          <div className="mt-2 flex items-center gap-3">
            <Button
              variant="secondary"
              onClick={addParticipant}
              disabled={participantIds.length >= maxTeams || participantIds.length >= teams.length}
            >
              Add a team
            </Button>
            <span className="text-xs text-slate-400">
              {participantIds.length >= maxTeams
                ? `A trade can involve at most ${maxTeams} teams.`
                : `Up to ${maxTeams} teams can be involved.`}
            </span>
          </div>
        )}
      </div>

      <div className={`grid gap-4 ${COLUMN_GRID[participants.length] || ''}`}>
        {participants.map((team) => (
          <TeamColumn
            key={team.id}
            league={info}
            team={team}
            mine={team.id === myTeamId}
            selected={selectedIds}
            onToggle={toggle}
            onSync={() => sync.mutate()}
            syncing={sync.isPending}
          />
        ))}
      </div>

      <div className="sticky bottom-0 mt-4 border-t py-3 backdrop-blur border-slate-800 bg-slate-950/95">
        {/* Stacked on a phone. Chip groups plus two buttons in one row leaves
            each about 120px, which truncates every player name. */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-6">
          <div className="flex min-w-0 flex-1 flex-wrap gap-6">
            {participants.map((team) => (
              <SelectionChips
                key={team.id}
                label={`${team.name} gives up`}
                entries={chosen.filter((c) => c.fromTeamId === team.id)}
                onRemove={remove}
                onRoute={route}
                targets={destinationsFor(participantIds, team.id).map((id) => ({
                  id,
                  label: nameFor(id),
                }))}
              />
            ))}
          </div>

          <div className="flex items-center gap-2 sm:shrink-0 sm:self-center">
            {chosen.length > 0 && (
              <Button variant="secondary" onClick={() => setSelection(new Map())}>
                Clear
              </Button>
            )}
            <Button
              className="flex-1 sm:flex-none"
              disabled={!ready || create.isPending}
              onClick={() => create.mutate()}
            >
              {create.isPending ? 'Evaluating…' : 'Evaluate trade'}
            </Button>
          </div>
        </div>

        {!ready && (
          <p className="mt-2 text-xs text-slate-400">
            Pick at least one player from two different teams. You can select as
            many as you like.
          </p>
        )}

        {/* The server derives the teams in a trade from the players that move,
            so a team nobody trades with is simply not in it. Better to say so
            than to show a column that turns out not to count. */}
        {ready && idle.length > 0 && (
          <p className="mt-2 text-xs text-amber-300">
            {idle.map(nameFor).join(' and ')}{' '}
            {idle.length === 1 ? 'is' : 'are'} not giving up or receiving anyone,
            so {idle.length === 1 ? 'it' : 'they'} will not be part of this trade.
          </p>
        )}

        <div className="mt-2">
          <ErrorMessage error={create.error} />
          <ErrorMessage error={sync.error} />
        </div>
      </div>
    </Layout>
  )
}
